import { useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  CourierInboxPopupMenu,
  defaultDarkTheme,
  defaultLightTheme,
  markAsRead,
  useCourier,
} from "@trycourier/courier-react";
import type {
  CourierInboxListItemActionFactoryProps,
  CourierInboxListItemFactoryProps,
  CourierInboxMenuButtonFactoryProps,
  CourierInboxStateEmptyFactoryProps,
  CourierInboxTheme,
  InboxMessage,
} from "@trycourier/courier-react";
import { Bell } from "lucide-react";
import { useFetcher } from "~/hooks/useFetcher";
import type { ResolvedTheme } from "~/hooks/useNextGenTheme";
import { cn } from "~/lib/utils";
import { type NotificationsToken, notificationsTokenPath } from "~/services/notifications";
import { isApiError } from "~/utils/apiClient";

// A link from a notification is followed only when it stays inside the app;
// `//host` is protocol-relative and would leave it.
const isInAppPath = (href: string) => href.startsWith("/") && !href.startsWith("//");

// A 4xx on the token request is a missing permission or a backend with notifications
// off, which retrying cannot fix, and SWR would otherwise retry it forever, logging
// each time. A 5xx or a network failure may pass, so those are still retried.
const shouldRetryTokenRequest = (error: unknown) => !(isApiError(error) && error.status < 500);

const NOTIFICATIONS_LABEL = "Notifications";
const ARCHIVED_LABEL = "Archived";

// The Ask AI popover's title size (`text-sm`), so the two headers read alike.
const HEADER_TITLE_FONT_SIZE = "14px";

// Courier marks unread notifications and the unread count in blue. The app's
// purple is a token that holds its value in light and dark, so one string serves both.
const UNREAD_COLOR = "var(--primary)";

// The popup is drawn on the same surface as the Ask AI popover, in both modes, so the
// caret that points at the bell (see index.css) can be drawn in the same token and read
// as part of it. The header and list are set too: they paint over the window itself.
const POPUP_SURFACE = "var(--popover)";

// How far below the top of the bell the popup starts. The caret is drawn in index.css
// 6px above this, so the two numbers move together; 10px under a 36px bell leaves it
// the same gap the Ask AI popover has.
const POPUP_TOP = "46px";

// Courier wraps the menu button in its own button and tints it on hover. The
// bell draws its own hover state, so Courier's is cleared. The theme sets wording
// and the unread color and leaves the rest to Courier, which merges it over its
// defaults.
//
// It merges `font` as a whole, though, so a bare size would drop the color and the
// title would be drawn in `red`. The default color differs between light and dark, so
// each mode's theme starts from that mode's own defaults.
const buildNotificationsTheme = (defaults: CourierInboxTheme): CourierInboxTheme => ({
  popup: {
    button: {
      backgroundColor: "transparent",
      hoverBackgroundColor: "transparent",
      activeBackgroundColor: "transparent",
    },
    window: { backgroundColor: POPUP_SURFACE, border: "1px solid var(--border)" },
  },
  inbox: {
    header: {
      backgroundColor: POPUP_SURFACE,
      filters: {
        font: { ...defaults.inbox?.header?.filters?.font, size: HEADER_TITLE_FONT_SIZE },
        inbox: { text: NOTIFICATIONS_LABEL },
        archive: { text: ARCHIVED_LABEL },
        unreadIndicator: { backgroundColor: UNREAD_COLOR },
      },
      menus: {
        filters: { inbox: { text: NOTIFICATIONS_LABEL }, archive: { text: ARCHIVED_LABEL } },
      },
    },
    list: { backgroundColor: POPUP_SURFACE, item: { unreadIndicatorColor: UNREAD_COLOR } },
  },
});

const lightNotificationsTheme = buildNotificationsTheme(defaultLightTheme);
const darkNotificationsTheme = buildNotificationsTheme(defaultDarkTheme);

// Courier's theme words the empty state once for both feeds, so it is drawn here to say
// which feed is empty.
const EMPTY_STATE_TEXT = {
  inbox: "No notifications yet",
  archive: "No archived notifications",
} as const;

const renderEmptyState = (props: CourierInboxStateEmptyFactoryProps | undefined | null) => (
  <p className="flex h-full w-full items-center justify-center p-6 text-center text-base font-medium text-foreground">
    {EMPTY_STATE_TEXT[props?.feedType ?? "inbox"]}
  </p>
);

interface BellIconProps {
  colored: boolean;
  unreadCount: number;
}

// A span, not a Button: Courier puts this inside a button of its own. Styled like
// the help button beside it, so the two read as a pair on either header.
const BellIcon = ({ colored, unreadCount }: BellIconProps) => (
  <span
    data-testid="notification-bell-icon"
    className={cn(
      "relative inline-flex size-9 cursor-pointer items-center justify-center rounded-full text-muted-foreground duration-300 hover:bg-black/[0.06] hover:text-foreground popup-open:bg-black/[0.06] popup-open:text-foreground",
      colored &&
        "text-white/80 hover:bg-white/20 hover:text-white popup-open:bg-white/20 popup-open:text-white"
    )}
  >
    <Bell className="size-5" aria-hidden="true" />
    <span className="sr-only">Notifications</span>
    {unreadCount > 0 && (
      <span
        data-testid="notification-unread-dot"
        className="absolute top-2 right-2.5 size-2 rounded-full bg-destructive"
      />
    )}
  </span>
);

interface NotificationBellProps {
  // See ManifoldNextGenHelpPopover's `colored` — the same colored-home-hero treatment.
  colored?: boolean;
  resolvedTheme?: ResolvedTheme;
}

export const NotificationBell = ({
  colored = false,
  resolvedTheme = "light",
}: NotificationBellProps) => {
  const navigate = useNavigate();
  // `shared` is Courier's singleton, so it is safe as an effect dependency; the
  // hook's own `auth` helpers are recreated on every render.
  const { shared: courier } = useCourier();
  // One token per session: it lasts a day, and refetching on focus would
  // sign the inbox out and back in each time the tab regains it.
  const { data: token, error } = useFetcher<NotificationsToken>(notificationsTokenPath, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    shouldRetryOnError: shouldRetryTokenRequest,
  });

  useEffect(() => {
    if (error) {
      console.error("Failed to load the notification token", error);
    }
  }, [error]);

  useEffect(() => {
    if (!token) {
      return;
    }
    courier.signIn({ userId: token.user_id, jwt: token.token });
    return () => {
      courier.signOut();
    };
  }, [courier, token]);

  // Opening a notification, from its row or from its button, reads it and follows
  // its link when that stays inside the app. Courier does neither itself: its
  // click handling only records the click, and it never navigates for a link.
  const openNotification = (message: InboxMessage, href: string | undefined) => {
    markAsRead(message);
    if (href && isInAppPath(href)) {
      navigate(href);
    }
  };

  const openMessageLink = ({ message }: CourierInboxListItemFactoryProps) => {
    openNotification(message, message.actions?.[0]?.href);
  };

  const openActionLink = ({ message, action }: CourierInboxListItemActionFactoryProps) => {
    openNotification(message, action.href);
  };

  // Stable between renders: Courier re-mounts the button whenever this changes.
  const renderBellButton = useCallback(
    (buttonProps: CourierInboxMenuButtonFactoryProps | undefined | null) => (
      <BellIcon colored={colored} unreadCount={buttonProps?.unreadCount ?? 0} />
    ),
    [colored]
  );

  if (!token) {
    return null;
  }

  return (
    <CourierInboxPopupMenu
      // Right-aligned, so the popup opens leftward from the bell instead of
      // running off the right edge of the window.
      popupAlignment="top-right"
      top={POPUP_TOP}
      mode={resolvedTheme}
      lightTheme={lightNotificationsTheme}
      darkTheme={darkNotificationsTheme}
      renderMenuButton={renderBellButton}
      renderEmptyState={renderEmptyState}
      onMessageClick={openMessageLink}
      onMessageActionClick={openActionLink}
    />
  );
};
